// 기본 템플릿의 회의 정보 항목 (백엔드 lib/summarize.js 의 INFO_FIELDS 와 동일)
// 내 템플릿을 올리면 양식에 있는 항목({ key, label })으로 바뀐다
export const INFO_FIELDS = [
  { key: 'projectName', label: '프로젝트명', placeholder: '예) 회의록 자동 정리' },
  { key: 'author', label: '작성자', placeholder: '예) 홍길동' },
  { key: 'datetime', label: '일시', placeholder: '예) 2026년 10월 19일(월) 14:00 ~ 15:00' },
  { key: 'location', label: '회의 장소', placeholder: '예) 본사 3층 회의실 B' },
  { key: 'attendees', label: '참석자', placeholder: '예) 김민수(팀장), 이서연(기획)' },
  { key: 'topic', label: '회의주제', placeholder: '예) 주간 서비스 기획 회의' },
];

export const CONTENT_LABEL = '회의 내용';
export const ACTION_LABEL = '액션아이템';

// 기본 템플릿용 $라벨$ 예시
export const PLACEHOLDER_LABELS = [...INFO_FIELDS.map(({ label }) => label), CONTENT_LABEL, ACTION_LABEL];

export const EMPTY_INFO = Object.fromEntries(INFO_FIELDS.map(({ key }) => [key, '']));

// 값이 길어지는 항목은 한 줄 전체를 쓴다 (백엔드 lib/docx.js 의 isWideField 와 동일)
export function isWideField(label) {
  return /참석|주제|안건|회의명|제목|내용|목적|비고/.test(label);
}

// 보통 항목은 두 개씩, 긴 항목은 한 줄에 하나씩 배치한다
export function layoutInfoRows(fields) {
  const rows = [];
  let pending = null;
  fields.forEach((field) => {
    if (isWideField(field.label)) {
      if (pending) rows.push([pending]);
      pending = null;
      rows.push([field]);
    } else if (pending) {
      rows.push([pending, field]);
      pending = null;
    } else {
      pending = field;
    }
  });
  if (pending) rows.push([pending]);
  return rows;
}

// 초 → "1시간 12분", "12분", "45초"
export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
  if (m > 0) return `${m}분`;
  return `${s}초`;
}

// 브라우저에서 녹음 길이를 미리 읽는다 (읽지 못하는 형식이면 null - 서버에서 다시 확인한다)
export function probeAudioDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? audio.duration : null);
    audio.onerror = () => done(null);
    setTimeout(() => done(null), 5000);
    audio.src = url;
  });
}

const normalize = (label) => label.replace(/\s/g, '');

// 항목 목록이 바뀔 때 이미 입력한 값은 같은 이름의 항목으로 옮긴다
export function remapInfo(info, prevFields, nextFields) {
  const valueByLabel = Object.fromEntries(prevFields.map(({ key, label }) => [normalize(label), info[key] || '']));
  return Object.fromEntries(nextFields.map(({ key, label }) => [key, valueByLabel[normalize(label)] || '']));
}

export function sameFields(a, b) {
  return JSON.stringify(a.map(({ key, label }) => [key, label])) === JSON.stringify(b.map(({ key, label }) => [key, label]));
}

// 복사용 텍스트
export function minutesToText(minutes, fields) {
  const info = fields.map(({ key, label }) => `${label}: ${minutes.info[key] || '-'}`);
  const sections = minutes.sections.map((section, index) =>
    [`${index + 1}. ${section.title}`, ...section.items.map((item) => `  - ${item}`)].join('\n'),
  );
  // 액션아이템이 없으면 항목을 넣지 않는다
  const actionItems =
    minutes.actionItems.length > 0
      ? [
          '',
          '[액션아이템] 담당자 | 할 일 | 기한',
          ...minutes.actionItems.map(({ owner, task, due }) => `- ${owner} | ${task} | ${due}`),
        ]
      : [];

  const infoBlock = info.length > 0 ? ['[회의 정보]', ...info, ''] : [];
  return [...infoBlock, '[회의 내용]', ...sections, ...actionItems].join('\n');
}
